import StatusBadge from "../../intelligence/StatusBadge";
import { trustScoreTone } from "../../../../utils/sourceTrustLabels";

const SourceQualityDetailDrawer = ({
  detail,
  loading,
  onClose,
  onRecalculate,
  recalculating,
}) => (
  <aside className="source-quality-drawer" aria-label="Source quality details">
    <div className="source-quality-drawer__header">
      <div>
        <p className="eyebrow">Source quality</p>
        <h2>{detail?.displayName || "Loading…"}</h2>
      </div>
      <button type="button" onClick={onClose}>
        Close
      </button>
    </div>

    {loading ? <p className="intel-meta">Loading details…</p> : null}

    {!loading && detail ? (
      <div className="source-quality-drawer__body">
        <div className="source-quality-drawer__hero">
          <StatusBadge
            label={`${detail.trustLabel} · ${detail.trustScore ?? "—"}`}
            tone={trustScoreTone(detail.trustScore)}
          />
          <StatusBadge label={detail.trustConfidence || "UNKNOWN"} tone="neutral" />
          <p className="muted">
            {detail.trustSampleSizeRuns} runs · {detail.trustSampleSizeListings} active listings
            {detail.trustLastCalculatedAt
              ? ` · ${new Date(detail.trustLastCalculatedAt).toLocaleString()}`
              : ""}
          </p>
        </div>

        {detail.formula ? (
          <section>
            <h3>Trust formula</h3>
            <p className="muted">Version {detail.formula.version}</p>
            <ul className="source-quality-drawer__weights">
              {Object.entries(detail.formula.weights || {}).map(([key, weight]) => (
                <li key={key}>
                  {key.replace(/([A-Z])/g, " $1").trim()}: {(weight * 100).toFixed(0)}%
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {detail.topIssues?.length ? (
          <section>
            <h3>Key issues</h3>
            <ul>
              {detail.topIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {detail.errorSeverity?.topIssues?.length ? (
          <section>
            <h3>Error severity drivers</h3>
            <p className="muted">
              Score {detail.metrics?.errorSeverityScore ?? "—"} ·{" "}
              {detail.summary?.errorSeverityLabel || "—"}
            </p>
            <ul>
              {detail.errorSeverity.topIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
            {detail.errorSeverity.errorCounts ? (
              <dl className="source-quality-drawer__facts">
                {Object.entries(detail.errorSeverity.errorCounts).map(([level, count]) => (
                  <div key={level}>
                    <dt>{level}</dt>
                    <dd>{count}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </section>
        ) : null}

        <section>
          <h3>Sub-scores</h3>
          <div className="source-quality-drawer__grid">
            {Object.entries(detail.signals || {}).map(([key, signal]) => (
              <article key={key} className="panel-card source-quality-signal-card">
                <p className="eyebrow">{key.replace(/_/g, " ")}</p>
                <strong>{signal.score ?? "—"}</strong>
                <p className="muted">{signal.explanation}</p>
              </article>
            ))}
          </div>
        </section>

        {detail.summary ? (
          <section>
            <h3>Summary metrics</h3>
            <dl className="source-quality-drawer__facts">
              {Object.entries(detail.summary).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {detail.history?.length ? (
          <section>
            <h3>Recent trend</h3>
            <ul className="source-quality-drawer__history">
              {detail.history.map((entry) => (
                <li key={entry.calculatedAt}>
                  {new Date(entry.calculatedAt).toLocaleString()} — {entry.trustLabel} (
                  {entry.trustScore ?? "—"})
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <button type="button" onClick={onRecalculate} disabled={recalculating}>
          {recalculating ? "Recalculating…" : "Recalculate this source"}
        </button>
      </div>
    ) : null}
  </aside>
);

export default SourceQualityDetailDrawer;
